import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  ChatTurn,
  CreatorChatActionRow,
  CreatorChatModal,
  CreatorChatPlatform,
  LlmHealthStatus,
  creatorChatAdminService,
  creatorChatWidgetUrl,
  matchCreatorChatActionVideo,
  pollChatVideoJob,
  preloadCreatorChatVideos,
  getCreatorChatActionVideoPlayState,
  warmCreatorChatVideoOnGesture,
  isCreatorChatVideoHot,
} from '../../services/creatorChatService';
import {
  playCreatorChatAudioUrl,
  stopCreatorChatAudio,
  unlockCreatorChatAudioGesture,
  isCreatorChatAudioBusy,
  type CreatorChatAudioRefs,
} from '../../services/creatorChatAudio';
import {
  startCreatorChatProactiveSchedule,
  type ProactiveNudgeKind,
} from '../../services/creatorChatProactive';
import './AdminComponents.css';
import AdminCreatorChatActionsPanel from './AdminCreatorChatActionsPanel';
import AdminCreatorChatAvatarGenerator from './AdminCreatorChatAvatarGenerator';

type FormState = {
  name: string;
  title: string;
  subtitle: string;
  welcomeMessage: string;
  systemPrompt: string;
  writingStyle: string;
  personaTimezone: string;
  awakeFromHour: string;
  awakeToHour: string;
  fallbackMessage: string;
  ctaLabel: string;
  accentColor: string;
  avatarImageUrl: string;
  targetPlatform: CreatorChatPlatform;
  launcherPosition: 'BOTTOM_RIGHT' | 'BOTTOM_LEFT';
  creatorLabel: string;
  actionsEnabled: boolean;
  idleVideoUrl: string;
  voiceEnabled: boolean;
  ttsVoice: string;
  ttsSpeed: string;
};

/** OpenAI TTS voices — gender tags are typical perception, not official OpenAI labels. */
const TTS_VOICE_OPTIONS: { id: string; label: string }[] = [
  { id: 'alloy', label: 'alloy — neutral' },
  { id: 'ash', label: 'ash — male' },
  { id: 'ballad', label: 'ballad — male' },
  { id: 'coral', label: 'coral — female' },
  { id: 'echo', label: 'echo — male' },
  { id: 'fable', label: 'fable — neutral' },
  { id: 'onyx', label: 'onyx — male (deep)' },
  { id: 'nova', label: 'nova — female (default)' },
  { id: 'sage', label: 'sage — female / neutral' },
  { id: 'shimmer', label: 'shimmer — female' },
  { id: 'verse', label: 'verse — male' },
];

const EMPTY_FORM: FormState = {
  name: '',
  title: '',
  subtitle: '',
  welcomeMessage: '',
  systemPrompt:
    'You are a warm, flirty, on-brand AI persona for a creator chatting with fans. Stay in character, keep replies concise (1-3 sentences), never break character, and never discuss illegal topics.',
  writingStyle: '',
  personaTimezone: '',
  awakeFromHour: '',
  awakeToHour: '',
  fallbackMessage: "Sorry, I'm having trouble replying right now. Please try again in a moment.",
  ctaLabel: 'Chat with me',
  accentColor: '#086afb',
  avatarImageUrl: '',
  targetPlatform: 'BOTH',
  launcherPosition: 'BOTTOM_RIGHT',
  creatorLabel: '',
  actionsEnabled: false,
  idleVideoUrl: '',
  voiceEnabled: false,
  ttsVoice: 'nova',
  ttsSpeed: '1',
};

function toForm(m: CreatorChatModal): FormState {
  return {
    name: m.name || '',
    title: m.title || '',
    subtitle: m.subtitle || '',
    welcomeMessage: m.welcomeMessage || '',
    systemPrompt: m.systemPrompt || '',
    writingStyle: m.writingStyle || '',
    personaTimezone: m.personaTimezone || '',
    awakeFromHour: m.awakeFromHour != null ? String(m.awakeFromHour) : '',
    awakeToHour: m.awakeToHour != null ? String(m.awakeToHour) : '',
    fallbackMessage: m.fallbackMessage || '',
    ctaLabel: m.ctaLabel || 'Chat with me',
    accentColor: m.accentColor || '#086afb',
    avatarImageUrl: m.avatarImageUrl || '',
    targetPlatform: (m.targetPlatform as CreatorChatPlatform) || 'BOTH',
    launcherPosition: (m.launcherPosition as FormState['launcherPosition']) || 'BOTTOM_RIGHT',
    creatorLabel: m.creatorLabel || '',
    actionsEnabled: !!m.actionsEnabled,
    idleVideoUrl: m.idleVideoUrl || '',
    voiceEnabled: !!m.voiceEnabled,
    ttsVoice: m.ttsVoice || 'nova',
    ttsSpeed: m.ttsSpeed != null ? String(m.ttsSpeed) : '1',
  };
}

/** Blank clears the waking window, which the backend reads as awake around the clock. */
function hourOrNull(raw: string): number | null {
  if (!raw.trim()) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 && n <= 23 ? n : null;
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
  value: String(h),
  label: `${String(h).padStart(2, '0')}:00`,
}));

/** Zones the personas are most often based in; the backend accepts any IANA id. */
const TIMEZONE_OPTIONS = [
  'Europe/London',
  'Europe/Lisbon',
  'Europe/Madrid',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Bucharest',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Asia/Dubai',
  'Asia/Bangkok',
  'Asia/Manila',
  'Australia/Sydney',
];

const Field: React.FC<{
  label: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
      {label}
    </label>
    {children}
    {hint ? <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0' }}>{hint}</p> : null}
  </div>
);

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  fontSize: 13,
  boxSizing: 'border-box',
  color: '#1e293b',
  background: '#fff',
  outline: 'none',
  fontFamily: 'inherit',
};

type TriggerRingItem = {
  id: number;
  label: string;
  fullLabel: string;
  videoUrl: string;
};

function primaryTriggerPhrase(action: CreatorChatActionRow): string {
  const fromSynonyms = (action.synonyms || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (fromSynonyms[0]) return fromSynonyms[0];
  if (action.label && action.label.trim()) return action.label.trim();
  return (action.actionSlug || 'clip').replace(/_/g, ' ');
}

function truncateTriggerLabel(text: string, max = 14): string {
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function polarPoint(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

/** Donut wedge for side dartboard (angles in degrees, 0 = top). */
function donutSlicePath(
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  startDeg: number,
  endDeg: number
): string {
  const sweep = endDeg - startDeg;
  const large = sweep > 180 ? 1 : 0;
  const p0 = polarPoint(cx, cy, outerR, startDeg);
  const p1 = polarPoint(cx, cy, outerR, endDeg);
  const p2 = polarPoint(cx, cy, innerR, endDeg);
  const p3 = polarPoint(cx, cy, innerR, startDeg);
  return [
    `M ${p0.x} ${p0.y}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${p1.x} ${p1.y}`,
    `L ${p2.x} ${p2.y}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${p3.x} ${p3.y}`,
    'Z',
  ].join(' ');
}

const TRIGGER_RING_COLORS = [
  '#086afb',
  '#0dca9d',
  '#e7038e',
  '#fbb445',
  '#7c3aed',
  '#06b6d4',
  '#f97316',
  '#64748b',
];

const AvatarDropZone: React.FC<{
  value: string;
  disabled?: boolean;
  uploadsAllowed?: boolean;
  onChange: (url: string) => void;
}> = ({ value, disabled, uploadsAllowed = true, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file: File) => {
    if (!uploadsAllowed) {
      setError('Confirm content upload rights before uploading an avatar.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Only image files are accepted');
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const url = await creatorChatAdminService.uploadAvatar(file);
      onChange(url);
    } catch (e: any) {
      setError(`Upload failed (${e?.message || 'error'}). You can paste a URL instead.`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (disabled) return;
          const file = e.dataTransfer.files?.[0];
          if (file) void upload(file);
        }}
        onClick={() => !disabled && inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? '#086afb' : '#cbd5e1'}`,
          borderRadius: 12,
          padding: 16,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: dragOver ? '#eff6ff' : '#f8fafc',
          minHeight: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
        }}
      >
        {value ? (
          <img
            src={value}
            alt="Avatar"
            style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e2e8f0' }}
          />
        ) : null}
        <div style={{ fontSize: 13, color: '#64748b' }}>
          {uploading ? 'Uploading…' : value ? 'Click or drop to replace avatar' : 'Drag & drop avatar image'}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          disabled={disabled}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void upload(file);
            e.target.value = '';
          }}
        />
      </div>
      <input
        style={{ ...inputStyle, marginTop: 8 }}
        placeholder="Or paste image URL"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      {error ? <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{error}</p> : null}
    </div>
  );
};

const AdminCreatorChatModalBuilder: React.FC = () => {
  const adminUser = useSelector((s: RootState) => s.auth.adminUser);
  const permissions: string[] = adminUser?.permissions || [];
  const isSuper = adminUser?.role === 'SUPER_ADMIN';
  const canManage = isSuper || permissions.includes('MANAGE_CREATOR_CHAT');
  const canView = canManage || permissions.includes('VIEW_CREATOR_CHAT');

  const [modals, setModals] = useState<CreatorChatModal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  const [testOpen, setTestOpen] = useState(false);
  const [testHistory, setTestHistory] = useState<ChatTurn[]>([]);
  const [testInput, setTestInput] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testLlmError, setTestLlmError] = useState<string | null>(null);
  const [testVideoHint, setTestVideoHint] = useState<string | null>(null);
  const [testActionVideo, setTestActionVideo] = useState<string | null>(null);
  const [testShowIdle, setTestShowIdle] = useState(true);
  /** Idle/action video has painted a frame — until then keep avatar visible (avoid black box). */
  const [testIdleReady, setTestIdleReady] = useState(false);
  const [testActionReady, setTestActionReady] = useState(false);
  const [testActions, setTestActions] = useState<CreatorChatActionRow[]>([]);
  const [testRingTriggerId, setTestRingTriggerId] = useState<number | null>(null);
  const [testPendingAudioUrl, setTestPendingAudioUrl] = useState<string | null>(null);
  const testVideoRef = useRef<HTMLVideoElement>(null);
  const testIdleVideoRef = useRef<HTMLVideoElement>(null);
  const testPlayedUrlRef = useRef<string | null>(null);
  const testAudioRef = useRef<HTMLAudioElement | null>(null);
  const testAudioCtxRef = useRef<AudioContext | null>(null);
  const testAudioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const testAudioGenRef = useRef(0);
  const testAudioQueueRef = useRef<string[]>([]);
  const testAudioPlayingRef = useRef(false);
  const testAudioIdleWaitersRef = useRef<Array<() => void>>([]);
  const testMessagesRef = useRef<HTMLDivElement | null>(null);
  const testMessagesEndRef = useRef<HTMLDivElement | null>(null);
  const testNudgeBusyRef = useRef(false);
  const testBusyRef = useRef(false);
  const testHistoryRef = useRef<ChatTurn[]>([]);

  testHistoryRef.current = testHistory;
  testBusyRef.current = testBusy;

  const testAudioRefs = (): CreatorChatAudioRefs => ({
    ctxRef: testAudioCtxRef,
    htmlAudioRef: testAudioRef,
    sourceRef: testAudioSourceRef,
    generationRef: testAudioGenRef,
    queueRef: testAudioQueueRef,
    playingRef: testAudioPlayingRef,
    idleWaitersRef: testAudioIdleWaitersRef,
  });

  const testTriggerRingItems = useMemo((): TriggerRingItem[] => {
    return (testActions || [])
      .filter(
        (a) =>
          a.active !== false &&
          a.showOnTriggerWheel !== false &&
          !!(a.videoUrl || '').trim() &&
          !(a.videoUrl || '').includes('example.com')
      )
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id)
      .map((a) => {
        const fullLabel = primaryTriggerPhrase(a);
        return {
          id: a.id,
          fullLabel,
          label: truncateTriggerLabel(fullLabel),
          videoUrl: (a.videoUrl || '').trim(),
        };
      });
  }, [testActions]);

  const testWheelCenterItem = useMemo((): TriggerRingItem | null => {
    const center = (testActions || []).find(
      (a) =>
        a.active !== false &&
        a.triggerWheelCenter === true &&
        !!(a.videoUrl || '').trim() &&
        !(a.videoUrl || '').includes('example.com')
    );
    if (!center) return null;
    const fullLabel =
      (center.label && center.label.trim()) ||
      primaryTriggerPhrase(center) ||
      'Special';
    return {
      id: center.id,
      fullLabel,
      label: truncateTriggerLabel(fullLabel, 10),
      videoUrl: (center.videoUrl || '').trim(),
    };
  }, [testActions]);

  const testWheelVisible =
    form.actionsEnabled && (testTriggerRingItems.length > 0 || !!testWheelCenterItem);
  const [copied, setCopied] = useState(false);
  const [llmHealth, setLlmHealth] = useState<LlmHealthStatus | null>(null);
  const [llmHealthBusy, setLlmHealthBusy] = useState(false);
  const [contentRightsAcknowledged, setContentRightsAcknowledged] = useState(false);

  const uploadsAllowed = canManage && contentRightsAcknowledged;

  // Hide admin mobile bottom nav + lock scroll while Test persona fills the phone screen
  useEffect(() => {
    if (!testOpen) {
      stopCreatorChatAudio(testAudioRefs());
      setTestPendingAudioUrl(null);
      return undefined;
    }
    document.body.classList.add('creator-chat-test-open');
    return () => {
      document.body.classList.remove('creator-chat-test-open');
      stopCreatorChatAudio(testAudioRefs());
    };
  }, [testOpen]);

  // Keep latest Test persona messages in view
  useEffect(() => {
    if (!testOpen) return;
    const box = testMessagesRef.current;
    if (!box) return;
    // Prefer scrollTop so we don't scroll the outer admin page/modal incorrectly.
    requestAnimationFrame(() => {
      box.scrollTop = box.scrollHeight;
    });
  }, [testOpen, testHistory, testBusy, testPendingAudioUrl, testLlmError]);

  useEffect(() => {
    if (!testOpen || !testActionVideo) return undefined;
    const frame = requestAnimationFrame(() => {
      if (!isCreatorChatVideoHot(testActionVideo)) return;
      setTestActionReady(true);
      void testVideoRef.current?.play().catch(() => undefined);
    });
    return () => cancelAnimationFrame(frame);
  }, [testOpen, testActionVideo]);

  useEffect(() => {
    if (!testOpen || !selectedId) return undefined;

    const runProactiveNudge = async (kind: ProactiveNudgeKind) => {
      if (testNudgeBusyRef.current || testBusyRef.current) return;
      testNudgeBusyRef.current = true;
      try {
        const result = await creatorChatAdminService.testProactiveNudge(
          selectedId,
          kind,
          testHistoryRef.current,
          form.systemPrompt
        );
        if (result.llmFailed || result.error) {
          setTestLlmError(result.error || 'AI unavailable — check server LLM configuration.');
        }
        if (result.reply) {
          setTestHistory((h) => [...h, { role: 'assistant', content: result.reply }]);
        }
        if (result.audioUrl) {
          playTestAudio(result.audioUrl);
        } else if (result.ttsSkipped && form.voiceEnabled) {
          setTestVideoHint(`Voice skipped: ${result.ttsSkipped}`);
        }
        if (result.action?.videoUrl) {
          playTestActionVideo(result.action.videoUrl);
        }
      } catch {
        /* proactive is best-effort */
      } finally {
        testNudgeBusyRef.current = false;
      }
    };

    return startCreatorChatProactiveSchedule({
      enabled: true,
      active: testOpen,
      hasWelcome: false,
      isBusy: () =>
        testBusyRef.current ||
        testNudgeBusyRef.current ||
        isCreatorChatAudioBusy(testAudioRefs()),
      onNudge: runProactiveNudge,
    });
  }, [testOpen, selectedId, form.systemPrompt, form.voiceEnabled]);

  const selected = useMemo(
    () => modals.find((m) => m.id === selectedId) || null,
    [modals, selectedId]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await creatorChatAdminService.list();
      setModals(list);
    } catch (e: any) {
      setError(`Failed to load modals (${e?.message || 'error'})`);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshLlmHealth = useCallback(async () => {
    setLlmHealthBusy(true);
    try {
      const h = await creatorChatAdminService.llmHealth(true);
      setLlmHealth(h);
    } catch {
      setLlmHealth({
        enabled: false,
        configured: false,
        provider: 'unknown',
        model: '',
        keySource: 'none',
        ok: false,
        reachable: false,
        message: 'Could not reach LLM health endpoint',
      });
    } finally {
      setLlmHealthBusy(false);
    }
  }, []);

  useEffect(() => {
    if (canView) {
      void load();
      void refreshLlmHealth();
    }
  }, [canView, load, refreshLlmHealth]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
  };

  const startCreate = () => {
    setSelectedId(null);
    setForm(EMPTY_FORM);
    setTestHistory([]);
    setTestOpen(false);
    setStatusMsg(null);
  };

  const selectModal = (m: CreatorChatModal) => {
    setSelectedId(m.id);
    setForm(toForm(m));
    setTestHistory([]);
    setTestOpen(false);
    setStatusMsg(null);
  };

  const save = async () => {
    if (!canManage) return;
    if (!form.name.trim()) {
      setStatusMsg('Name is required');
      return;
    }
    setSaving(true);
    setStatusMsg(null);
    try {
      const payload = {
        name: form.name.trim(),
        title: form.title.trim() || null,
        subtitle: form.subtitle.trim() || null,
        welcomeMessage: null,
        systemPrompt: form.systemPrompt.trim() || null,
        writingStyle: form.writingStyle.trim() || null,
        personaTimezone: form.personaTimezone.trim() || null,
        awakeFromHour: hourOrNull(form.awakeFromHour),
        awakeToHour: hourOrNull(form.awakeToHour),
        fallbackMessage: form.fallbackMessage.trim() || null,
        ctaLabel: form.ctaLabel.trim() || null,
        accentColor: form.accentColor.trim() || '#086afb',
        avatarImageUrl: form.avatarImageUrl.trim() || null,
        targetPlatform: form.targetPlatform,
        launcherPosition: form.launcherPosition,
        creatorLabel: form.creatorLabel.trim() || null,
        actionsEnabled: form.actionsEnabled,
        idleVideoUrl: form.idleVideoUrl.trim() || null,
        voiceEnabled: form.voiceEnabled,
        ttsVoice: form.ttsVoice.trim() || 'nova',
        ttsSpeed: Number(form.ttsSpeed) || 1,
      };
      let saved: CreatorChatModal;
      if (selectedId) {
        saved = await creatorChatAdminService.update(selectedId, payload);
      } else {
        saved = await creatorChatAdminService.create({ ...payload, name: payload.name });
      }
      await load();
      setSelectedId(saved.id);
      setForm(toForm(saved));
      setStatusMsg('Saved');
    } catch (e: any) {
      setStatusMsg(`Save failed (${e?.message || 'error'})`);
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async () => {
    if (!canManage || !selected) return;
    setSaving(true);
    try {
      const saved = selected.published
        ? await creatorChatAdminService.unpublish(selected.id)
        : await creatorChatAdminService.publish(selected.id);
      await load();
      setSelectedId(saved.id);
      setForm(toForm(saved));
      setStatusMsg(saved.published ? 'Published — fans can use the widget link' : 'Unpublished — widget is offline');
    } catch (e: any) {
      setStatusMsg(`Publish failed (${e?.message || 'error'})`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!canManage || !selected) return;
    if (!window.confirm(`Delete “${selected.name}”? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await creatorChatAdminService.remove(selected.id);
      setSelectedId(null);
      setForm(EMPTY_FORM);
      await load();
      setStatusMsg('Deleted');
    } catch (e: any) {
      setStatusMsg(`Delete failed (${e?.message || 'error'})`);
    } finally {
      setSaving(false);
    }
  };

  const playTestActionVideo = (url: string, triggerId?: number | null) => {
    if (!url) return;
    testPlayedUrlRef.current = url;
    preloadCreatorChatVideos([url]);
    const { ready } = getCreatorChatActionVideoPlayState(url);
    setTestShowIdle(false);
    setTestActionReady(ready);
    setTestActionVideo(url);
    setTestRingTriggerId(triggerId ?? null);
  };

  const onTestActionVideoReady = () => {
    setTestActionReady(true);
    void testVideoRef.current?.play().catch(() => undefined);
  };

  /** Call during a real click/keydown so later TTS play() is more likely to be allowed. */
  const unlockTestAudioGesture = () => {
    unlockCreatorChatAudioGesture(testAudioRefs());
  };

  const playTestAudio = (url?: string | null) => {
    if (!url) return;
    void playCreatorChatAudioUrl(url, testAudioRefs()).then((ok) => {
      if (ok) {
        setTestPendingAudioUrl(null);
      } else {
        setTestPendingAudioUrl(url);
        setTestVideoHint(null);
      }
    });
  };

  const hearPendingTestAudio = () => {
    const url = testPendingAudioUrl;
    if (!url) return;
    // Resume context in this click, then play — do not prime/pause the TTS element.
    unlockTestAudioGesture();
    void playCreatorChatAudioUrl(url, testAudioRefs()).then((ok) => {
      if (ok) {
        setTestPendingAudioUrl(null);
      }
    });
  };

  const playTriggerFromRing = (item: TriggerRingItem) => {
    if (!canManage || !selectedId || testBusy) return;
    unlockTestAudioGesture();
    void submitTestMessage(item.fullLabel, { ringItem: item });
  };

  const submitTestMessage = async (
    message: string,
    opts?: { ringItem?: TriggerRingItem }
  ) => {
    if (!canManage || !selectedId || !message.trim()) return;
    unlockTestAudioGesture();
    const trimmed = message.trim();
    const priorHistory = testHistory;
    setTestHistory([...priorHistory, { role: 'user' as const, content: trimmed }]);
    setTestBusy(true);
    setTestLlmError(null);
    setTestVideoHint(null);
    setTestPendingAudioUrl(null);

    // Wheel tap or typed message — start the clip immediately when we know it.
    if (opts?.ringItem) {
      playTestActionVideo(opts.ringItem.videoUrl, opts.ringItem.id);
    } else if (form.actionsEnabled) {
      const instantUrl = matchCreatorChatActionVideo(trimmed, testActions);
      if (instantUrl) {
        playTestActionVideo(instantUrl);
      }
    }

    try {
      // Persist in background so test-chat isn't blocked on Save.
      void creatorChatAdminService
        .update(selectedId, {
          name: form.name.trim() || selected?.name || 'Untitled',
          systemPrompt: form.systemPrompt,
          welcomeMessage: null,
          fallbackMessage: form.fallbackMessage,
          title: form.title,
          subtitle: form.subtitle,
          accentColor: form.accentColor,
          avatarImageUrl: form.avatarImageUrl || null,
          ctaLabel: form.ctaLabel,
          targetPlatform: form.targetPlatform,
          launcherPosition: form.launcherPosition,
          creatorLabel: form.creatorLabel || null,
          actionsEnabled: form.actionsEnabled,
          idleVideoUrl: form.idleVideoUrl || null,
          voiceEnabled: form.voiceEnabled,
          ttsVoice: form.ttsVoice || 'nova',
          ttsSpeed: Number(form.ttsSpeed) || 1,
        })
        .catch(() => undefined);

      const result = await creatorChatAdminService.testChat(
        selectedId,
        trimmed,
        priorHistory,
        form.systemPrompt
      );

      // Unlock composer immediately after LLM reply — fal poll continues in background.
      if (result.llmFailed || result.error) {
        setTestLlmError(result.error || 'AI unavailable — check server LLM configuration.');
        setTestHistory((h) => [
          ...h,
          {
            role: 'assistant',
            content: result.reply || `[System] AI could not reply. ${result.error || ''}`,
          },
        ]);
      } else {
        setTestHistory((h) => [...h, { role: 'assistant', content: result.reply }]);
      }

      if (result.audioUrl) {
        playTestAudio(result.audioUrl);
      } else if (result.ttsSkipped && form.voiceEnabled) {
        setTestVideoHint(`Voice skipped: ${result.ttsSkipped}`);
      }

      if (result.action?.videoUrl) {
        // Only switch if we didn't already start this clip (or start a different one).
        if (testPlayedUrlRef.current !== result.action.videoUrl) {
          playTestActionVideo(result.action.videoUrl);
        }
        setTestVideoHint(null);
      } else if (result.videoJob?.id && selectedId) {
        const jobId = result.videoJob.id;
        const modalId = selectedId;
        if (!testPlayedUrlRef.current) {
          setTestVideoHint('Generating clip from avatar — usually 30–120 seconds…');
        }
        // Tell Recent generations to pick up the new RUNNING job without a page refresh.
        window.dispatchEvent(new CustomEvent('creator-chat-jobs-changed'));
        // Poll fal in the background so the user can keep chatting.
        void (async () => {
          const url = await pollChatVideoJob(
            () => creatorChatAdminService.getVideoJob(modalId, jobId),
            {
              onTick: (s) => {
                if (s.status === 'FAILED') {
                  setTestVideoHint(s.error || 'Video generation failed.');
                }
                if (s.status === 'SUCCEEDED' || s.status === 'FAILED') {
                  window.dispatchEvent(new CustomEvent('creator-chat-jobs-changed'));
                }
              },
            }
          );
          window.dispatchEvent(new CustomEvent('creator-chat-jobs-changed'));
          if (url) {
            playTestActionVideo(url);
            setTestVideoHint(null);
          } else if (!testPlayedUrlRef.current) {
            setTestVideoHint((h) => (h?.startsWith('Generating') ? 'Video generation timed out or failed.' : h));
          }
        })();
      } else if (!testPlayedUrlRef.current && result.videoJobError) {
        setTestVideoHint(`Realtime video failed: ${result.videoJobError}`);
      } else if (!testPlayedUrlRef.current && result.videoGenSkipped) {
        setTestVideoHint(`Realtime video skipped: ${result.videoGenSkipped}`);
      } else if (!testPlayedUrlRef.current && form.actionsEnabled) {
        const lower = trimmed.toLowerCase();
        if (
          lower.includes('kiss') ||
          lower.includes('wave') ||
          lower.includes('dance') ||
          lower.includes('wink') ||
          lower.includes('blow') ||
          lower.includes('turn') ||
          lower.includes('tap') ||
          lower.includes('bum') ||
          lower.includes('bra') ||
          lower.includes('bikini') ||
          lower.includes('spank') ||
          lower.includes('undress') ||
          lower.includes('strip')
        ) {
          setTestVideoHint(
            'No matching uploaded clip found. Check trigger phrases on the action card, enable Video actions, Save, then retry — or wait for realtime fal generation if enabled.'
          );
        }
      }
    } catch (e: any) {
      setTestHistory((h) => [
        ...h,
        { role: 'assistant', content: `Test failed (${e?.message || 'error'})` },
      ]);
    } finally {
      setTestBusy(false);
    }
  };

  const sendTest = async () => {
    if (!canManage || !selectedId || !testInput.trim() || testBusy) return;
    unlockTestAudioGesture();
    const message = testInput.trim();
    setTestInput('');
    await submitTestMessage(message);
  };

  const copyWidgetLink = async () => {
    if (!selected?.publicId) return;
    const url = creatorChatWidgetUrl(selected.publicId);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy widget URL', url);
    }
  };

  if (!canView) {
    return (
      <div className="admin-section" style={{ padding: 24 }}>
        <h2 style={{ marginTop: 0 }}>Creator Chat Modals</h2>
        <p style={{ color: '#64748b' }}>You do not have permission to view this feature.</p>
      </div>
    );
  }

  return (
    <div className="admin-section creator-chat-page" style={{ padding: 20, maxWidth: 1200 }}>
      <div className="creator-chat-page-header" style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: '#0f172a' }}>Creator Chat Modals</h2>
          <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 13, maxWidth: 560 }}>
            Upload a creator avatar, configure the AI persona, test the chat bot, then publish a hosted
            widget link for Fanvue and OnlyFans.
          </p>
        </div>
        {canManage ? (
          <button
            type="button"
            onClick={startCreate}
            className="admin-btn admin-btn-primary"
            style={{ alignSelf: 'flex-start' }}
          >
            + New modal
          </button>
        ) : null}
      </div>

      {error ? (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: 12, borderRadius: 8, marginBottom: 12 }}>
          {error}
        </div>
      ) : null}
      {llmHealth ? (
        <div
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 8,
            background: llmHealth.ok ? '#ecfdf5' : '#fff7ed',
            border: `1px solid ${llmHealth.ok ? '#a7f3d0' : '#fed7aa'}`,
            color: llmHealth.ok ? '#065f46' : '#9a3412',
            fontSize: 13,
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <strong style={{ display: 'block', marginBottom: 2 }}>
              {llmHealth.ok ? 'AI text replies: ready' : 'AI text replies: needs attention'}
            </strong>
            <span style={{ opacity: 0.9 }}>
              {llmHealth.message ||
                `${llmHealth.provider} / ${llmHealth.model} · key: ${llmHealth.keySource}`}
            </span>
            {!llmHealth.ok ? (
              <div style={{ marginTop: 4, fontSize: 12, opacity: 0.85 }}>
                Action videos still work without LLM. Fund OpenAI billing or set{' '}
                <code>creator.chat.llm.openai.api-key</code> on the server.
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="admin-btn"
            disabled={llmHealthBusy}
            onClick={() => void refreshLlmHealth()}
            style={{ flexShrink: 0 }}
          >
            {llmHealthBusy ? 'Checking…' : 'Re-check AI'}
          </button>
        </div>
      ) : null}
      {statusMsg ? (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
          {statusMsg}
        </div>
      ) : null}

      <div className="creator-chat-layout">
        <div className="creator-chat-modals-list" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: 13 }}>
            Modals {loading ? '…' : `(${modals.length})`}
          </div>
          <div className="creator-chat-modals-list-scroll" style={{ maxHeight: 640, overflowY: 'auto' }}>
            {modals.length === 0 && !loading ? (
              <p style={{ padding: 14, fontSize: 13, color: '#94a3b8' }}>No modals yet.</p>
            ) : null}
            {modals.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => selectModal(m)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  borderBottom: '1px solid #f1f5f9',
                  background: selectedId === m.id ? '#eff6ff' : '#fff',
                  padding: '12px 14px',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{m.name}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  {m.targetPlatform || 'BOTH'} · {m.published ? 'Published' : 'Draft'}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="creator-chat-editor" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18 }}>
          <div className="creator-chat-editor-toolbar" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>
              {selectedId ? `Edit: ${form.name || 'Untitled'}` : 'Create new modal'}
            </h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {canManage ? (
                <button type="button" className="admin-btn admin-btn-primary" disabled={saving} onClick={() => void save()}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              ) : null}
              {canManage && selectedId ? (
                <button
                  type="button"
                  className="admin-btn"
                  disabled={saving}
                  onClick={() => {
                    unlockTestAudioGesture();
                    setTestOpen(true);
                    setTestLlmError(null);
                    setTestVideoHint(null);
                    setTestActionVideo(null);
                    setTestActionReady(false);
                    setTestShowIdle(true);
                    setTestIdleReady(false);
                    testPlayedUrlRef.current = null;
                    if (selectedId) {
                      void creatorChatAdminService
                        .listActions(selectedId)
                        .then((list) => {
                          setTestActions(list);
                          preloadCreatorChatVideos([
                            form.idleVideoUrl,
                            ...list.map((a) => a.videoUrl),
                          ]);
                        })
                        .catch(() => setTestActions([]));
                    }
                    if (form.idleVideoUrl) {
                      preloadCreatorChatVideos([form.idleVideoUrl, form.avatarImageUrl]);
                    }
                    window.setTimeout(() => {
                      const idle = testIdleVideoRef.current;
                      if (idle) {
                        void idle.play().catch(() => undefined);
                      }
                    }, 80);
                  }}
                >
                  Test persona
                </button>
              ) : null}
              {canManage && selected ? (
                <button type="button" className="admin-btn" disabled={saving} onClick={() => void togglePublish()}>
                  {selected.published ? 'Unpublish' : 'Publish'}
                </button>
              ) : null}
              {canManage && selected ? (
                <button type="button" className="admin-btn" disabled={saving} onClick={() => void remove()} style={{ color: '#b91c1c' }}>
                  Delete
                </button>
              ) : null}
            </div>
          </div>

          {selected?.publicId ? (
            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: 12,
                marginBottom: 16,
                fontSize: 12,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6, color: '#334155' }}>
                Widget link {selected.published ? '(live)' : '(offline until published)'}
              </div>
              <code style={{ wordBreak: 'break-all', color: '#0f172a' }}>
                {creatorChatWidgetUrl(selected.publicId)}
              </code>
              <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="admin-btn" onClick={() => void copyWidgetLink()}>
                  {copied ? 'Copied' : 'Copy link'}
                </button>
                <span style={{ color: '#64748b', alignSelf: 'center' }}>
                  Share on Fanvue bio / messages or OnlyFans profile / DMs
                </span>
              </div>
            </div>
          ) : (
            <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 0 }}>
              Save once to generate a public widget ID and link.
            </p>
          )}

          <div className="creator-chat-form-grid">
            <Field label="Internal name *">
              <input
                style={inputStyle}
                value={form.name}
                disabled={!canManage}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="e.g. Aitana Fanvue chat"
              />
            </Field>
            <Field label="Creator label" hint="Optional handle for organization">
              <input
                style={inputStyle}
                value={form.creatorLabel}
                disabled={!canManage}
                onChange={(e) => setField('creatorLabel', e.target.value)}
                placeholder="@creator"
              />
            </Field>
            <Field label="Modal title">
              <input
                style={inputStyle}
                value={form.title}
                disabled={!canManage}
                onChange={(e) => setField('title', e.target.value)}
              />
            </Field>
            <Field label="Subtitle">
              <input
                style={inputStyle}
                value={form.subtitle}
                disabled={!canManage}
                onChange={(e) => setField('subtitle', e.target.value)}
              />
            </Field>
            <Field label="Target platform">
              <select
                style={inputStyle}
                value={form.targetPlatform}
                disabled={!canManage}
                onChange={(e) => setField('targetPlatform', e.target.value as CreatorChatPlatform)}
              >
                <option value="BOTH">Fanvue + OnlyFans</option>
                <option value="FANVUE">Fanvue</option>
                <option value="ONLYFANS">OnlyFans</option>
              </select>
            </Field>
            <Field label="Launcher position">
              <select
                style={inputStyle}
                value={form.launcherPosition}
                disabled={!canManage}
                onChange={(e) => setField('launcherPosition', e.target.value as FormState['launcherPosition'])}
              >
                <option value="BOTTOM_RIGHT">Bottom right</option>
                <option value="BOTTOM_LEFT">Bottom left</option>
              </select>
            </Field>
            <Field label="Accent color">
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="color"
                  value={form.accentColor || '#086afb'}
                  disabled={!canManage}
                  onChange={(e) => setField('accentColor', e.target.value)}
                  style={{ width: 44, height: 40, border: 'none', background: 'transparent' }}
                />
                <input
                  style={inputStyle}
                  value={form.accentColor}
                  disabled={!canManage}
                  onChange={(e) => setField('accentColor', e.target.value)}
                />
              </div>
            </Field>
            <Field label="CTA / launcher label">
              <input
                style={inputStyle}
                value={form.ctaLabel}
                disabled={!canManage}
                onChange={(e) => setField('ctaLabel', e.target.value)}
              />
            </Field>
          </div>

          <Field label="Avatar image" hint="Used as chat avatar / branding (not auto-vision)">
            {canManage ? (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10, fontSize: 12, color: '#475569', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={contentRightsAcknowledged}
                  onChange={(e) => setContentRightsAcknowledged(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span>
                  I confirm the creator (or rights holder) owns the avatar and video content uploaded for this modal.
                </span>
              </label>
            ) : null}
            <AvatarDropZone
              value={form.avatarImageUrl}
              disabled={!canManage}
              uploadsAllowed={uploadsAllowed}
              onChange={(url) => setField('avatarImageUrl', url)}
            />
            <AdminCreatorChatAvatarGenerator
              canManage={canManage}
              currentAvatarUrl={form.avatarImageUrl || ''}
              onPickAvatar={(url) => setField('avatarImageUrl', url)}
            />
          </Field>


          <Field label="System prompt / persona" hint="Defines how the AI chat bot behaves">
            <textarea
              style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }}
              value={form.systemPrompt}
              disabled={!canManage}
              onChange={(e) => setField('systemPrompt', e.target.value)}
            />
          </Field>

          <Field
            label="Writing style"
            hint="How she types, not what she says. Casing, emoji, abbreviations, the odd typo. Leave blank for a neutral texting style."
          >
            <textarea
              style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }}
              value={form.writingStyle}
              disabled={!canManage}
              placeholder="e.g. all lowercase, barely any full stops, uses 'x' to sign off, favourite emoji are 🙈 and 😭, sometimes types too fast and corrects it with *word"
              onChange={(e) => setField('writingStyle', e.target.value)}
            />
          </Field>

          <Field
            label="Where she lives / when she's awake"
            hint="Lets her know what time it is where she supposedly is, so she's half asleep at 4am instead of uniformly chirpy. Leave the hours blank to keep her awake around the clock."
          >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select
                style={{ ...inputStyle, flex: '1 1 180px' }}
                value={form.personaTimezone}
                disabled={!canManage}
                onChange={(e) => setField('personaTimezone', e.target.value)}
              >
                <option value="">No timezone (never mentions the time)</option>
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz.replace('_', ' ')}
                  </option>
                ))}
              </select>
              <select
                style={{ ...inputStyle, flex: '0 1 120px' }}
                value={form.awakeFromHour}
                disabled={!canManage || !form.personaTimezone}
                onChange={(e) => setField('awakeFromHour', e.target.value)}
              >
                <option value="">Up from…</option>
                {HOUR_OPTIONS.map((h) => (
                  <option key={h.value} value={h.value}>
                    Up from {h.label}
                  </option>
                ))}
              </select>
              <select
                style={{ ...inputStyle, flex: '0 1 120px' }}
                value={form.awakeToHour}
                disabled={!canManage || !form.personaTimezone}
                onChange={(e) => setField('awakeToHour', e.target.value)}
              >
                <option value="">Until…</option>
                {HOUR_OPTIONS.map((h) => (
                  <option key={h.value} value={h.value}>
                    Until {h.label}
                  </option>
                ))}
              </select>
            </div>
          </Field>

          <Field label="Fallback message" hint="Shown if the LLM is unavailable">
            <input
              style={inputStyle}
              value={form.fallbackMessage}
              disabled={!canManage}
              onChange={(e) => setField('fallbackMessage', e.target.value)}
            />
          </Field>

          <div
            style={{
              marginBottom: 16,
              padding: 12,
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              background: '#f8fafc',
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
              <input
                type="checkbox"
                checked={form.voiceEnabled}
                disabled={!canManage}
                onChange={(e) => setField('voiceEnabled', e.target.checked)}
              />
              Enable voice (TTS)
            </label>
            <p style={{ margin: '6px 0 10px', fontSize: 11, color: '#64748b' }}>
              When on, each AI reply is spoken (OpenAI TTS). Requires server{' '}
              <code style={{ fontSize: 10 }}>creator.chat.features.voice=true</code> and an OpenAI key. Avatar video stays
              as idle/action clips for now — lip-sync is a later sprint.
            </p>
            {form.voiceEnabled ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10 }}>
                <Field label="TTS voice">
                  <select
                    style={inputStyle}
                    value={form.ttsVoice}
                    disabled={!canManage}
                    onChange={(e) => setField('ttsVoice', e.target.value)}
                  >
                    {TTS_VOICE_OPTIONS.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Speed" hint="0.25–4">
                  <input
                    style={inputStyle}
                    type="number"
                    min={0.25}
                    max={4}
                    step={0.05}
                    value={form.ttsSpeed}
                    disabled={!canManage}
                    onChange={(e) => setField('ttsSpeed', e.target.value)}
                  />
                </Field>
              </div>
            ) : null}
          </div>

          {!canManage ? (
            <p style={{ fontSize: 12, color: '#94a3b8' }}>Read-only — ask for MANAGE_CREATOR_CHAT to edit.</p>
          ) : null}

          {selectedId ? (
            <AdminCreatorChatActionsPanel
              modalId={selectedId}
              canManage={canManage}
              uploadsAllowed={uploadsAllowed}
              avatarImageUrl={form.avatarImageUrl || ''}
              actionsEnabled={form.actionsEnabled}
              idleVideoUrl={form.idleVideoUrl}
              onActionsEnabledChange={(v) => setField('actionsEnabled', v)}
              onIdleVideoUrlChange={(url) => setField('idleVideoUrl', url)}
            />
          ) : null}
        </div>
      </div>

      {testOpen && selectedId && typeof document !== 'undefined'
        ? createPortal(
        <div className="admin-modal-overlay creator-chat-test-overlay" onClick={() => setTestOpen(false)}>
          <div
            className="admin-modal creator-chat-test-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Test AI persona"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 720,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              className="admin-modal-header"
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {form.avatarImageUrl ? (
                  <img
                    src={form.avatarImageUrl}
                    alt=""
                    style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : null}
                <div>
                  <strong>{form.title || form.name || 'Test persona'}</strong>
                  <div style={{ fontSize: 12, color: '#64748b' }}>Draft preview — not visible to fans</div>
                </div>
              </div>
              <button type="button" className="admin-btn" onClick={() => setTestOpen(false)}>
                Close
              </button>
            </div>
            <div
              className="admin-modal-body"
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                paddingBottom: 0,
              }}
            >
              <div className="creator-chat-test-scroll">
              <div
                className={`creator-chat-test-stage${
                  form.actionsEnabled && testTriggerRingItems.length > 0
                    ? ' creator-chat-test-stage--with-board'
                    : ''
                }`}
              >
                <div className="creator-chat-test-media">
                  {form.avatarImageUrl ? (
                    <img
                      src={form.avatarImageUrl}
                      alt=""
                      className="creator-chat-test-media-layer"
                      style={{ zIndex: 0 }}
                    />
                  ) : null}
                  {form.actionsEnabled && form.idleVideoUrl && testShowIdle && !testActionVideo ? (
                    <video
                      ref={testIdleVideoRef}
                      key={form.idleVideoUrl}
                      src={form.idleVideoUrl}
                      poster={form.avatarImageUrl || undefined}
                      preload="auto"
                      className="creator-chat-test-media-layer"
                      style={{
                        zIndex: 1,
                        opacity: testIdleReady ? 1 : 0,
                        transition: 'opacity 120ms linear',
                      }}
                      autoPlay
                      loop
                      muted
                      playsInline
                      onCanPlay={() => {
                        setTestIdleReady(true);
                        void testIdleVideoRef.current?.play().catch(() => undefined);
                      }}
                      onPlaying={() => setTestIdleReady(true)}
                      onError={() => setTestIdleReady(false)}
                    />
                  ) : null}
                  {testActionVideo ? (
                    <video
                      ref={testVideoRef}
                      key={testActionVideo}
                      src={testActionVideo}
                      poster={form.avatarImageUrl || undefined}
                      preload="auto"
                      className="creator-chat-test-media-layer creator-chat-test-media-layer--action"
                      style={{ zIndex: 2 }}
                      autoPlay
                      muted
                      playsInline
                      onLoadStart={() => {
                        if (!isCreatorChatVideoHot(testActionVideo)) {
                          setTestActionReady(false);
                        }
                      }}
                      onLoadedData={onTestActionVideoReady}
                      onCanPlay={onTestActionVideoReady}
                      onPlaying={() => setTestActionReady(true)}
                      onStalled={() => {
                        void testVideoRef.current?.play().catch(() => undefined);
                      }}
                      onError={() => {
                        setTestActionReady(false);
                        setTestVideoHint('Could not play this clip — check the Action Video URL.');
                      }}
                      onEnded={() => {
                        testPlayedUrlRef.current = null;
                        setTestRingTriggerId(null);
                        setTestActionVideo(null);
                        setTestActionReady(false);
                        setTestShowIdle(true);
                        setTestIdleReady(false);
                        setTestVideoHint(null);
                        window.setTimeout(() => {
                          const idle = testIdleVideoRef.current;
                          if (idle) void idle.play().catch(() => undefined);
                        }, 40);
                      }}
                    />
                  ) : null}
                  {!form.avatarImageUrl && !form.idleVideoUrl && !testActionVideo ? (
                    <div className="creator-chat-test-media-empty">
                      Upload an avatar or idle video to preview the model
                    </div>
                  ) : null}
                </div>

                {testWheelVisible ? (
                  <aside className="creator-chat-test-dartboard" aria-label="Trigger phrases">
                    <div className="creator-chat-test-dartboard-wheel">
                      <svg
                        className="creator-chat-test-dartboard-svg"
                        viewBox="0 0 100 100"
                        role="presentation"
                      >
                        {testTriggerRingItems.map((item, index) => {
                          const n = testTriggerRingItems.length;
                          const gap = n > 1 ? Math.min(2.5, 18 / n) : 0;
                          const slice = 360 / n;
                          const startDeg = index * slice + gap / 2;
                          const endDeg = (index + 1) * slice - gap / 2;
                          const active = testRingTriggerId === item.id;
                          const fill = TRIGGER_RING_COLORS[index % TRIGGER_RING_COLORS.length];
                          return (
                            <path
                              key={`wedge-${item.id}`}
                              d={donutSlicePath(50, 50, 18, 42, startDeg, endDeg)}
                              fill={fill}
                              opacity={active ? 1 : 0.82}
                              className={`creator-chat-test-dartboard-wedge${
                                active ? ' is-active' : ''
                              }`}
                              style={{ cursor: 'pointer' }}
                              onPointerDown={() => warmCreatorChatVideoOnGesture(item.videoUrl)}
                              onClick={() => playTriggerFromRing(item)}
                            >
                              <title>{item.fullLabel}</title>
                            </path>
                          );
                        })}
                        <circle
                          cx="50"
                          cy="50"
                          r="16.5"
                          fill="#0f172a"
                          className={
                            testWheelCenterItem
                              ? `creator-chat-test-dartboard-center-fill${
                                  testRingTriggerId === testWheelCenterItem.id ? ' is-active' : ''
                                }`
                              : undefined
                          }
                        />
                        {!testWheelCenterItem ? (
                          <text
                            x="50"
                            y="49"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill="#94a3b8"
                            fontSize="5.5"
                            fontWeight="700"
                            style={{ pointerEvents: 'none', userSelect: 'none' }}
                          >
                            Triggers
                          </text>
                        ) : null}
                      </svg>
                      {testWheelCenterItem ? (
                        <button
                          type="button"
                          title={testWheelCenterItem.fullLabel}
                          aria-label={`Play center clip: ${testWheelCenterItem.fullLabel}`}
                          className={`creator-chat-test-dartboard-center${
                            testRingTriggerId === testWheelCenterItem.id ? ' is-active' : ''
                          }`}
                          onPointerDown={() => warmCreatorChatVideoOnGesture(testWheelCenterItem.videoUrl)}
                          onClick={() => playTriggerFromRing(testWheelCenterItem)}
                        >
                          {testWheelCenterItem.label}
                        </button>
                      ) : null}
                      {testTriggerRingItems.map((item, index) => {
                        const n = testTriggerRingItems.length;
                        const mid = (index + 0.5) * (360 / n);
                        const pos = polarPoint(50, 50, 46.5, mid);
                        const active = testRingTriggerId === item.id;
                        const short = truncateTriggerLabel(item.fullLabel, n > 10 ? 8 : 11);
                        return (
                          <button
                            key={`dart-lbl-${item.id}`}
                            type="button"
                            title={item.fullLabel}
                            className={`creator-chat-test-dartboard-label${
                              active ? ' is-active' : ''
                            }`}
                            style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                            onPointerDown={() => warmCreatorChatVideoOnGesture(item.videoUrl)}
                            onClick={() => playTriggerFromRing(item)}
                          >
                            {short}
                          </button>
                        );
                      })}
                    </div>
                    <p className="creator-chat-test-trigger-hint">
                      {testWheelCenterItem
                        ? 'Tap center or a wedge to play'
                        : 'Tap a wedge to play'}
                    </p>
                  </aside>
                ) : null}

                {form.actionsEnabled && testTriggerRingItems.length === 0 ? (
                  <div className="creator-chat-test-trigger-empty">
                    No trigger clips yet — add Action Videos with phrases, then reopen Test persona.
                  </div>
                ) : null}
              </div>
              {!form.actionsEnabled ? (
                <div
                  style={{
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    color: '#92400e',
                    padding: '8px 10px',
                    borderRadius: 8,
                    fontSize: 12,
                    marginBottom: 8,
                  }}
                >
                  <strong>Text-only mode.</strong> Enable <em>Video actions</em> below, upload kiss/wave clips, Save, then
                  commands like &quot;blow me a kiss&quot; will play video.
                </div>
              ) : null}
              {testVideoHint ? (
                <div
                  style={{
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    color: '#92400e',
                    padding: '8px 10px',
                    borderRadius: 8,
                    fontSize: 12,
                    marginBottom: 8,
                  }}
                >
                  {testVideoHint}
                </div>
              ) : null}
              {testLlmError ? (
                <div
                  style={{
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#b91c1c',
                    padding: '10px 12px',
                    borderRadius: 8,
                    fontSize: 12,
                    marginBottom: 10,
                    lineHeight: 1.45,
                  }}
                >
                  <strong>AI unavailable:</strong> {testLlmError}
                </div>
              ) : null}
              <div
                ref={testMessagesRef}
                className="creator-chat-test-messages"
                style={{
                  flex: '1 1 140px',
                  minHeight: 140,
                  overflowY: 'auto',
                  padding: 12,
                  background: '#f8fafc',
                  borderRadius: 8,
                }}
              >
                {testHistory.map((t, i) => (
                  <div
                    key={`${t.role}-${i}`}
                    style={{
                      display: 'flex',
                      justifyContent: t.role === 'user' ? 'flex-end' : 'flex-start',
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        maxWidth: '80%',
                        padding: '8px 12px',
                        borderRadius: 12,
                        fontSize: 13,
                        background: t.role === 'user' ? form.accentColor || '#086afb' : '#fff',
                        color: t.role === 'user' ? '#fff' : '#0f172a',
                        border: t.role === 'user' ? 'none' : '1px solid #e2e8f0',
                      }}
                    >
                      {t.content}
                    </div>
                  </div>
                ))}
                {testBusy ? (
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>Persona is typing…</div>
                ) : null}
                <div ref={testMessagesEndRef} />
              </div>
              </div>
              <div className="creator-chat-test-footer">
              {testPendingAudioUrl ? (
                <button
                  type="button"
                  className="creator-chat-test-hear-reply"
                  onClick={hearPendingTestAudio}
                  style={{
                    background: form.accentColor || '#086afb',
                  }}
                >
                  Tap to hear reply
                </button>
              ) : null}
              <div className="creator-chat-test-composer" style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <input
                  style={inputStyle}
                  value={testInput}
                  disabled={testBusy}
                  placeholder="Type a test message…"
                  onChange={(e) => setTestInput(e.target.value)}
                  onFocus={unlockTestAudioGesture}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void sendTest();
                    }
                  }}
                />
                <button
                  type="button"
                  className="admin-btn admin-btn-primary"
                  disabled={testBusy || !testInput.trim()}
                  onClick={() => void sendTest()}
                >
                  Send
                </button>
              </div>
              <button
                type="button"
                className="admin-btn creator-chat-test-reset"
                style={{ marginTop: 8, alignSelf: 'flex-start', flexShrink: 0 }}
                onClick={() => {
                  setTestLlmError(null);
                  setTestActionVideo(null);
                  setTestActionReady(false);
                  setTestShowIdle(true);
                  setTestIdleReady(false);
                  testPlayedUrlRef.current = null;
                  setTestHistory([]);
                  window.setTimeout(() => {
                    const idle = testIdleVideoRef.current;
                    if (idle) void idle.play().catch(() => undefined);
                  }, 40);
                }}
              >
                Reset conversation
              </button>
            </div>
            </div>
          </div>
        </div>,
        document.body
      )
        : null}
    </div>
  );
};

export default AdminCreatorChatModalBuilder;
